import { Phone, MapPin, Camera, Flower2, Fence, TreeDeciduous } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: Camera,
    title: 'Garden transformations are your advert',
    text: 'A muddy patch turned into a family garden is the most persuasive thing you own — but only if people see it. A proper project gallery puts your best transformations in front of every customer who looks you up.',
  },
  {
    icon: MapPin,
    title: 'You show up for “landscaper near me”',
    text: 'A complete Google Business Profile with the right categories, service areas and photos is what puts a local landscaper on the map. We set it up properly and guide you through Google’s verification.',
  },
  {
    icon: Phone,
    title: 'Spring and summer enquiries don’t wait',
    text: 'Landscaping demand arrives in waves. When the sun comes out, customers contact the landscapers they can reach fastest — click-to-call, WhatsApp and a simple enquiry form on every page.',
  },
];

const WORK_TYPES = [
  {
    icon: Flower2,
    title: 'Full garden transformations',
    text: 'Design-and-build projects worth thousands, planned for months. Before-and-after galleries of finished gardens do the selling long before you visit to quote.',
  },
  {
    icon: Fence,
    title: 'Patios, decking and fencing',
    text: 'The steady, high-demand work that fills a diary. Clear service pages with real photos mean customers know exactly what you cover — and what your finish looks like.',
  },
  {
    icon: TreeDeciduous,
    title: 'Regular garden maintenance',
    text: 'Recurring work that smooths out the seasons. A professional online presence turns one-off jobs into year-round customers.',
  },
];

const LANDSCAPER_FAQS = [
  {
    q: 'Landscaping is seasonal. Is a website worth it?',
    a: 'It matters more because of the seasonality. Customers plan garden projects in late winter and early spring, searching and shortlisting weeks before they call. If you’re visible and credible online before the season starts, the spring wave of enquiries comes to you — and maintenance work keeps things ticking over the rest of the year.',
  },
  {
    q: 'My best advert is before-and-after photos. Can the site be built around those?',
    a: 'That’s exactly how we’d build it. Decorating and landscaping are the trades where transformation photos do the heavy lifting, so your gallery is front and centre. Phone photos taken in decent light are genuinely all we need — wide shots before you start and after you finish.',
  },
  {
    q: 'I cover several towns and villages. Can the site reflect that?',
    a: 'Yes. Your site lists the areas you cover in plain language, and your Google profile is set up as a service-area business, so you’re visible across your whole patch rather than pinned to one address.',
  },
  {
    q: 'I’m on site in all weathers. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, your project photos from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function LandscapersPage() {
  return (
    <>
      <Seo
        title="Websites for Landscapers — Fill Your Diary Year-Round | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK landscapers and gardeners. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-landscapers"
        image={IMAGES.landscaperHero.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="landscapers-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for landscapers
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              You transform gardens. <span className="text-lime">We’ll transform how customers find you.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a landscaper needs to be found and booked locally: a professional
              website with a project gallery that shows off your transformations, a complete Google Business Profile
              and branded social pages — for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="landscapers-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="landscapers-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.landscaperHero.url}
              alt={IMAGES.landscaperHero.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a landscaper's online presence needs */}
      <section className="bg-offwhite" data-testid="landscapers-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how landscaping work is won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a landscaper’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="landscapers-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  Big projects and steady work
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to win the projects and smooth out the seasons
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  Landscaping businesses live on a mix of big transformations and reliable repeat work. Your website
                  sells both — the dream garden for the planner, the dependable pair of hands for the regulars.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.landscaperDetail.url}
                  alt={IMAGES.landscaperDetail.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="landscapers-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit a landscaping business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Most landscapers start with Look Professional: a proper website with a project gallery, plus
              the full Google and social setup.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Landscaper FAQs */}
      <section className="bg-offwhite" data-testid="landscapers-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions landscapers ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy landscapers
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={LANDSCAPER_FAQS} testid="landscapers-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="landscapers-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready to fill your diary <span className="text-lime">before the season starts?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your landscaping business and the areas you cover — we’ll recommend the right starting
                point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="landscapers-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
